import React, { useEffect, useCallback, useState } from 'react';
import { TransformControls } from '@react-three/drei';
import { useTransform } from '../../../../contexts/TransformContext';
import * as THREE from 'three';
import { Html } from '@react-three/drei';

export function EnhancedTransformControls({ 
    object, 
    id, 
    onTransformEnd,
    enabled = true,
    showHelpers = true,
    uniformScale = true,
    minScale = 0.001,
    maxScale = Infinity,
    showTransformInfo = true
}) {
    const {
        transformMode,
        setTransformMode,
        startTransform,
        endTransform,
        snapToGrid,
        snapValueToGrid,
        toggleSnapToGrid
    } = useTransform();

    const [lockedAxis, setLockedAxis] = useState(null);
    const [initialTransform, setInitialTransform] = useState(null);
    const [showInfo, setShowInfo] = useState(false);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (!enabled) return;
            
            switch (event.code) {
                case 'KeyT':
                    setTransformMode('translate');
                    break;
                case 'KeyR':
                    setTransformMode('rotate');
                    break;
                case 'KeyS':
                    setTransformMode('scale');
                    break;
                case 'KeyG':
                    event.preventDefault();
                    toggleSnapToGrid();
                    break;
                // Add axis locking shortcuts
                case 'KeyX':
                    setLockedAxis(prev => prev === 'x' ? null : 'x');
                    break;
                case 'KeyY':
                    setLockedAxis(prev => prev === 'y' ? null : 'y');
                    break;
                case 'KeyZ':
                    setLockedAxis(prev => prev === 'z' ? null : 'z');
                    break;
                // Hold Shift to show transform info
                case 'ShiftLeft':
                case 'ShiftRight':
                    setShowInfo(true);
                    break;
            }
        };

        const handleKeyUp = (event) => {
            if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
                setShowInfo(false);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.addEventListener('keydown', handleKeyPress);
            window.addEventListener('keyup', handleKeyUp);
        };
    }, [enabled, setTransformMode, toggleSnapToGrid]);

    const handleChange = useCallback(() => {
        if (!object) return;

        // Store initial transform on first change
        if (!initialTransform) {
            setInitialTransform({
                position: object.position.clone(),
                rotation: object.rotation.clone(),
                scale: object.scale.clone()
            });
        }

        // Handle uniform scaling
        if (transformMode === 'scale' && uniformScale) {
            const scale = object.scale;
            // Use the smallest scale value to determine the new scale
            const minCurrentScale = Math.min(scale.x, scale.y, scale.z);
            const clampedScale = Math.max(minScale, Math.min(maxScale, minCurrentScale));
            object.scale.set(clampedScale, clampedScale, clampedScale);
        } else if (transformMode === 'scale') {
            // For non-uniform scaling, clamp each axis individually
            object.scale.set(
                Math.max(minScale, Math.min(maxScale, object.scale.x)),
                Math.max(minScale, Math.min(maxScale, object.scale.y)),
                Math.max(minScale, Math.min(maxScale, object.scale.z))
            );
        }

        // Apply grid snapping and axis locking
        if (transformMode === 'translate') {
            const newPosition = object.position.clone();

            if (snapToGrid) {
                newPosition.set(
                    snapValueToGrid(newPosition.x),
                    snapValueToGrid(newPosition.y),
                    snapValueToGrid(newPosition.z)
                );
            }

            // Apply axis locking
            if (lockedAxis && initialTransform) {
                switch (lockedAxis) {
                    case 'x':
                        newPosition.y = initialTransform.position.y;
                        newPosition.z = initialTransform.position.z;
                        break;
                    case 'y':
                        newPosition.x = initialTransform.position.x;
                        newPosition.z = initialTransform.position.z;
                        break;
                    case 'z':
                        newPosition.x = initialTransform.position.x;
                        newPosition.y = initialTransform.position.y;
                        break;
                }
            }

            object.position.copy(newPosition);
        }

        // Apply axis locking for rotation
        if (transformMode === 'rotate' && lockedAxis && initialTransform) {
            const currentRotation = object.rotation;
            switch (lockedAxis) {
                case 'x':
                    currentRotation.y = initialTransform.rotation.y;
                    currentRotation.z = initialTransform.rotation.z;
                    break;
                case 'y':
                    currentRotation.x = initialTransform.rotation.x;
                    currentRotation.z = initialTransform.rotation.z;
                    break;
                case 'z':
                    currentRotation.x = initialTransform.rotation.x;
                    currentRotation.y = initialTransform.rotation.y;
                    break;
            }
        }
    }, [object, transformMode, uniformScale, snapToGrid, snapValueToGrid, minScale, maxScale, lockedAxis, initialTransform]);

    const handleDragStart = useCallback(() => {
        startTransform(id);
        setInitialTransform(null);
    }, [id, startTransform]);

    const handleDragEnd = useCallback(() => {
        if (!object) return;
        
        endTransform(id);
        setInitialTransform(null);
        
        if (onTransformEnd) {
            const transform = {
                position: object.position.toArray(),
                rotation: [
                    object.rotation.x,
                    object.rotation.y,
                    object.rotation.z
                ],
                scale: object.scale.toArray()
            };

            // Ensure values are finite and valid
            const isValidTransform = [...transform.position, ...transform.rotation, ...transform.scale]
                .every(val => Number.isFinite(val));

            if (isValidTransform) {
                onTransformEnd(transform);
            } else {
                console.error('Invalid transform values detected:', transform);
            }
        }
    }, [object, id, endTransform, onTransformEnd]);

    if (!enabled || !object) return null;

    return (
        <>
            <TransformControls
                object={object}
                mode={transformMode}
                size={0.75}
                showX={showHelpers && (!lockedAxis || lockedAxis === 'x')}
                showY={showHelpers && (!lockedAxis || lockedAxis === 'y')}
                showZ={showHelpers && (!lockedAxis || lockedAxis === 'z')}
                space="local"
                onMouseDown={handleDragStart}
                onMouseUp={handleDragEnd}
                onChange={handleChange}
            />

            {/* Transform Info Overlay */}
            {showTransformInfo && showInfo && (
                <Html
                    position={[0, -1, 0]}
                    center
                    style={{
                        background: 'rgba(0, 0, 0, 0.75)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        color: 'white',
                        fontSize: '12px',
                        whiteSpace: 'pre-line',
                        pointerEvents: 'none'
                    }}
                >
                    {`Position: ${object.position.toArray().map(v => v.toFixed(2)).join(', ')}
Rotation: ${object.rotation.toArray().map(v => (v * 180 / Math.PI).toFixed(1)).join(', ')}°
Scale: ${object.scale.toArray().map(v => v.toFixed(2)).join(', ')}
${lockedAxis ? `Locked to ${lockedAxis.toUpperCase()} axis` : ''}
${snapToGrid ? 'Grid snap enabled' : ''}`}
                </Html>
            )}
        </>
    );
} 